/**
 * Platform inspectors — RIGHT_INSP slot.
 *
 * Phase 13 P1 (June 2026)
 *
 * One file holds three tools that share the right-inspector slot:
 *
 *   * `ActorInspector`   — appears whenever a scene has actors AND
 *                          one is selected. Edits position, jersey,
 *                          role.
 *   * `EventInspector`   — appears whenever a scene has a timeline
 *                          AND an event is selected. Edits the
 *                          event's coaching_script + duration_ms.
 *   * `ReflectionPanel`  — appears for ``content_panel`` /
 *                          ``reflection`` scenes. Edits the
 *                          markdown body.
 *
 * The tools are registered separately so a tenant can disable any
 * one independently. Predicates share the same context shape; we
 * compose with `all` / `any` to keep each rule readable.
 */
import React from 'react';

import { DesignerTool } from '../DesignerTool';
import { useDesigner } from '../DesignerContext';
import {
  all, requiresCapability, requiresSceneType, any,
} from '../predicates';

// ────────────────────────────────────────────────────────────────────
// ActorInspector
// ────────────────────────────────────────────────────────────────────

function ActorInspector() {
  const {
    scene, selectedActorId, moveActor, mutateScene,
  } = useDesigner();

  if (!scene || !selectedActorId) return null;
  const actor = (scene.actors || []).find((a) => a.actor_id === selectedActorId);
  if (!actor) return null;

  const ip = (actor.initial_state || {}).position || { x: 0, y: 0 };

  const updateActor = (patch) => {
    const next = (scene.actors || []).map((a) =>
      a.actor_id === selectedActorId ? { ...a, ...patch } : a,
    );
    mutateScene({ ...scene, actors: next });
  };

  return (
    <div className="designer-tool" data-tool-id="platform.actor_inspector">
      <h3 className="designer-tool__heading">Actor: {actor.actor_id}</h3>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Role</label>
        <input
          type="text"
          className="designer-tool__input"
          value={actor.role || ''}
          aria-label="Actor role"
          onChange={(e) => updateActor({ role: e.target.value })}
        />
      </div>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Jersey</label>
        <input
          type="text"
          className="designer-tool__input"
          value={actor.jersey_number || ''}
          aria-label="Jersey number"
          onChange={(e) => updateActor({ jersey_number: e.target.value })}
        />
      </div>
      <div className="designer-tool__row designer-tool__row--inline">
        <label className="designer-tool__label">Position</label>
        <input
          type="number"
          step="0.5"
          className="designer-tool__input"
          aria-label="X position"
          value={ip.x}
          onChange={(e) => moveActor(actor.actor_id, parseFloat(e.target.value), ip.y)}
        />
        <input
          type="number"
          step="0.5"
          className="designer-tool__input"
          aria-label="Y position"
          value={ip.y}
          onChange={(e) => moveActor(actor.actor_id, ip.x, parseFloat(e.target.value))}
        />
      </div>
    </div>
  );
}

export const actorInspector = new DesignerTool({
  id:         'platform.actor_inspector',
  label:      'Actor inspector',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresCapability('has_actors'),
  Component:  ActorInspector,
  weight:     50,
  helpText:   'Edit the selected actor\'s role, jersey, and position.',
});

// ────────────────────────────────────────────────────────────────────
// EventInspector
// ────────────────────────────────────────────────────────────────────

function EventInspector() {
  const {
    scene, selectedEventIndex, mutateScene,
  } = useDesigner();

  const ev = scene?.timeline?.[selectedEventIndex];
  if (!scene || !ev) return null;

  const updateEvent = (patch) => {
    const next = scene.timeline.map((e, i) =>
      i === selectedEventIndex ? { ...e, ...patch } : e,
    );
    mutateScene({ ...scene, timeline: next });
  };

  return (
    <div className="designer-tool" data-tool-id="platform.event_inspector">
      <h3 className="designer-tool__heading">Event: {ev.event_id || `#${selectedEventIndex}`}</h3>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Coaching script</label>
        <textarea
          className="designer-tool__input designer-tool__input--textarea"
          rows={4}
          value={ev.coaching_script || ''}
          aria-label="Coaching script"
          onChange={(e) => updateEvent({ coaching_script: e.target.value })}
        />
      </div>
      <div className="designer-tool__row">
        <label className="designer-tool__label">Duration (ms)</label>
        <input
          type="number"
          className="designer-tool__input"
          value={ev.duration_ms || 0}
          step="100"
          min="0"
          aria-label="Event duration in milliseconds"
          onChange={(e) => updateEvent({ duration_ms: parseInt(e.target.value, 10) || 0 })}
        />
      </div>
    </div>
  );
}

export const eventInspector = new DesignerTool({
  id:         'platform.event_inspector',
  label:      'Event inspector',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: requiresCapability('has_timeline'),
  Component:  EventInspector,
  weight:     55,
  helpText:   'Edit the selected timeline event\'s script and duration.',
});

// ────────────────────────────────────────────────────────────────────
// ReflectionPanel — for content_panel / reflection scenes
// ────────────────────────────────────────────────────────────────────

function ReflectionPanel() {
  const { scene, setDomainMetadata } = useDesigner();
  if (!scene) return null;

  const meta = scene.domain_metadata || {};
  const body = meta.body_markdown || '';

  return (
    <div className="designer-tool" data-tool-id="platform.reflection_panel">
      <h3 className="designer-tool__heading">Reading body</h3>
      <textarea
        className="designer-tool__input designer-tool__input--textarea"
        rows={12}
        value={body}
        aria-label="Reading body markdown"
        placeholder="Markdown — supports #, **, *, [link](url), code blocks…"
        onChange={(e) => setDomainMetadata({ body_markdown: e.target.value })}
      />
    </div>
  );
}

export const reflectionPanel = new DesignerTool({
  id:         'platform.reflection_panel',
  label:      'Reading body',
  slot:       'RIGHT_INSP',
  category:   'inspector',
  applies_to: any(
    requiresSceneType('content_panel'),
    requiresSceneType('reflection'),
    requiresCapability('markdown'),
  ),
  Component:  ReflectionPanel,
  weight:     60,
  helpText:   'Edit the markdown body shown to learners.',
});

export const inspectorTools = [actorInspector, eventInspector, reflectionPanel];
export default inspectorTools;
